// Libretto's own chart kinds for <sb-echarts> (web/resources/vendor/sb-echarts.js).
//
// sb-echarts draws any ECharts option a server sends as JSON, but three of this app's
// charts draw through a custom series' renderItem – a function, which no server can
// send. The Go builders name them instead ({"kind": "gantt" | "energylabel" |
// "census", …}), and the builders here expand them in the page, with the element's
// context: the ECharts module, colours and tokens resolved in the element's scope,
// and the app's number format.
//
// They were methods of the old <tco-chart>; they read the same tokens and draw the
// same thing, only through that context instead of the class.
//
// Also here: numbers. Every figure the server renders groups thousands with an
// apostrophe (U+0027) in de/fr/it and a comma in English, exactly as
// internal/i18n Formatter does. Intl's de-CH writes U+2019 instead, which would put
// "3’500" on an axis one line below a server-rendered "3'500" – so the charts use the
// server's rule, not Intl's.

import { defineChartKind, setNumberFormat } from './vendor/sb-echarts.js';

type ChartConfig = Record<string, unknown>;

// What sb-echarts hands a kind: see "Charts whose shape is code" in its README.
interface KindContext {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  echarts: any;
  color(tokenOrCss: string): string;
  css(token: string): string;
  formatNumber(n: number): string;
  lang: string;
  width: number;
  height: number;
}

// The §81 ownership Gantt. Each car is one y-gutter row (GanttRow.name); a car can
// hold one or more bars (GanttBar) – a re-bought car (§85 buyback) draws several
// with a gap between them. Dates are epoch-ms; the date range and full tooltip text
// are pre-localized server-side. `bar.row` indexes into `rows`.
interface GanttRow {
  name: string;
}

interface GanttBar {
  row: number;
  start: number;
  end: number;
  ongoing: boolean;
  range: string;
  tip: string;
}

// The §107 Energieetikette. `classes` are A–G top to bottom; `mark` is the class the
// car carried when it was registered and `today` the class the same consumption earns
// on the current scale – the pair is the point, since the scale is re-cut every year and
// an unchanged car drifts downward. `today` may be empty (no drift, or unknown).
interface EnergyLabelConfig {
  classes: string[];
  mark: string;
  today: string;
  markLabel: string;
  todayLabel: string;
}

// The §114 census survival curve. One point per ASTRA stock census, `t` epoch-ms and `n`
// the number of that generation still on Swiss roads at that date; `tip` is the fully
// localized tooltip line, built server-side like the Gantt's.
//
// The x axis is TIME and not a category, which is load-bearing rather than tidy: the
// series is semi-annual from 2011 to 2023, then annual, and ASTRA published no census at
// all for 2023-07 or 2025-07. Evenly spaced points would draw those two gaps as ordinary
// intervals and quietly bend the curve.
interface CensusPointConfig {
  t: number;
  n: number;
  tip: string;
}

interface CensusConfig {
  points: CensusPointConfig[];
  name: string;
}

/**
 * The §114 census survival curve: how many of this generation were on Swiss roads at
 * each stock census since 2011.
 *
 * Two decisions are deliberate and neither is cosmetic.
 *
 * TIME AXIS, NOT CATEGORIES. The readings are semi-annual, then annual, with two
 * missing entirely (ASTRA published no 2023-07 or 2025-07 census). On a category axis
 * those gaps would be drawn as ordinary steps, which flattens a two-year interval into
 * the same width as a six-month one and misstates the slope.
 *
 * ZERO-BASED Y. A survival curve is read for its shape, and cropping the axis to the
 * data range makes an ordinary 5 % decline look like a collapse. The whole feature is
 * an argument against reading attrition too eagerly, so it should not draw one.
 *
 * The saw-tooth is left in. Every January reading sits below the July either side of
 * it, because a winter-deposited vehicle is absent from the census rather than flagged
 * in it, and for a seasonally-driven car that dip is real information about how the car
 * is used. Smoothing it away would be inventing data; the caption explains it instead.
 */
function censusConfig(cfg: ChartConfig, ctx: KindContext): ChartConfig {
  const c = cfg as unknown as CensusConfig;
  const points = Array.isArray(c.points) ? c.points : [];
  const accent = ctx.color('--accent');
  const muted = ctx.color('--text-3');
  const mono = ctx.css('--font-mono') || 'ui-monospace, monospace';
  const tips = new Map<number, string>();
  for (const p of points) tips.set(p.t, p.tip);

  return {
    ...cfg,
    grid: { left: 8, right: 14, top: 16, bottom: 22, containLabel: true },
    xAxis: {
      type: 'time',
      axisLabel: { color: muted, fontFamily: mono, fontSize: 11, hideOverlap: true },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      min: 0,
      axisLabel: { color: muted, fontFamily: mono, fontSize: 11 },
    },
    // One series, so a legend would only restate the caption above the chart.
    legend: { show: false },
    tooltip: {
      trigger: 'axis',
      // Server-built and already localized; fall back to the raw pair if a point
      // arrives without one rather than rendering "undefined".
      formatter: (params: unknown): string => {
        const list = Array.isArray(params) ? params : [params];
        const first = list[0] as { value?: unknown[] } | undefined;
        const v = first?.value as unknown[] | undefined;
        if (!v) return '';
        const t = new Date(v[0] as string | number).getTime();
        return tips.get(t) ?? String(v[1]);
      },
    },
    series: [{
      type: 'line',
      name: c.name,
      data: points.map((p) => [p.t, p.n]),
      symbol: 'circle',
      symbolSize: 5,
      showSymbol: points.length <= 40,
      lineStyle: { width: 2 },
      itemStyle: { color: accent },
      areaStyle: { opacity: 0.10 },
      // The first and last readings are the sentence the §93 badge already tells
      // ("N then, M today"), so they carry a label; the rest are the evidence.
      markPoint: points.length >= 2 ? {
        symbol: 'circle',
        symbolSize: 7,
        itemStyle: { color: accent },
        label: {
          position: 'top',
          fontFamily: mono,
          fontSize: 11,
          color: ctx.color('--text-2'),
          // Grouped like every other figure on the page. applyNumberLocale reaches
          // axis labels and tooltips but not markPoint labels, so "3232" would sit
          // one line below a y-axis reading "3'500".
          formatter: (p: { value?: unknown }) =>
            typeof p.value === 'number' ? ctx.formatNumber(p.value) : String(p.value ?? ''),
        },
        data: [
          // The opening label is pushed right of the axis: at the left edge it lands on
          // top of the topmost y-tick and the two numbers overprint.
          { coord: [points[0].t, points[0].n], value: points[0].n, label: { offset: [20, 2] } },
          { coord: [points[points.length - 1].t, points[points.length - 1].n], value: points[points.length - 1].n, label: { offset: [-6, 0] } },
        ],
      } : undefined,
    }],
  };
}

/**
 * The Energieetikette (§107): seven arrow bars, A shortest and dark green at the top,
 * G longest and red at the bottom, with the car's class called out.
 *
 * The bar COLOURS are the official label ramp and are intentionally NOT theme tokens –
 * they are the artefact being reproduced, and recolouring them would misrepresent a
 * federal document. Everything around them (type, callouts, axis) uses design-system
 * tokens so the panel still reads correctly in either theme.
 *
 * Two classes can be marked at once. That is the whole reason this chart exists: the
 * scale is absolute and re-cut annually, so the same unchanged car slides down it –
 * 86 % of 2020–24 registrations sit in a different class today than when they were
 * sold. Showing one letter hides that; showing both makes it self-evident.
 */
function energyLabelConfig(cfg: ChartConfig, ctx: KindContext): ChartConfig {
  const ec = ctx.echarts;
  const c = cfg as unknown as EnergyLabelConfig;
  const classes = c.classes && c.classes.length ? c.classes : ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
  // The EU/CH energy-label ramp, green through red.
  const ramp = ['#00a651', '#4cb847', '#bfd630', '#f6eb14', '#fdb913', '#f37021', '#ed1c24'];
  const ink = ctx.color('--text-1');
  const muted = ctx.color('--text-3');
  const surface = ctx.color('--surface-raised');
  const sans = ctx.css('--font-sans') || 'system-ui, sans-serif';
  const mono = ctx.css('--font-mono') || 'ui-monospace, monospace';

  const markIdx = classes.indexOf(c.mark);
  const todayIdx = classes.indexOf(c.today);

  // value: [rowIndex, barLength 0..1, marked?, isToday?]
  const data = classes.map((_, i) => ({
    value: [i, 0.34 + (0.66 * i) / Math.max(1, classes.length - 1), i === markIdx ? 1 : 0, i === todayIdx ? 1 : 0],
    itemStyle: { color: ramp[i % ramp.length] },
  }));

  const renderItem = (params: CustomRenderItemParams, api: CustomRenderItemAPI): unknown => {
    const idx = api.value(0) as number;
    const len = api.value(1) as number;
    const isMark = api.value(2) === 1;
    const isToday = api.value(3) === 1;
    const left = api.coord([0, idx]);
    const right = api.coord([len, idx]);
    const bandH = api.size([0, 1])[1];
    const h = Math.max(10, Math.min(30, bandH * 0.72));
    const x = left[0];
    const y = right[1] - h / 2;
    const w = Math.max(12, right[0] - left[0]);
    const tip = Math.min(h * 0.62, w * 0.5); // arrow head, the label's signature shape
    const fill = api.style().fill;

    const children: unknown[] = [{
      type: 'polygon',
      shape: {
        points: [
          [x, y], [x + w - tip, y], [x + w, y + h / 2],
          [x + w - tip, y + h], [x, y + h],
        ],
      },
      // A marked row is outlined rather than recoloured, so the official ramp survives.
      style: isMark || isToday
        ? { fill, stroke: ink, lineWidth: isMark ? 2 : 1, lineDash: isMark ? undefined : [3, 3] }
        : { fill },
    }, {
      // The letter sits at the RIGHT end of its bar, just inside the arrow tip, as on
      // the printed label – the staircase of letters is how the real thing is read.
      type: 'text',
      style: {
        x: x + w - tip - 6, y: y + h / 2, text: classes[idx],
        textAlign: 'right', textVerticalAlign: 'middle',
        fontFamily: sans, fontWeight: 700,
        fontSize: Math.max(12, Math.min(19, h * 0.78)),
        // Dark ink over the light middle of the ramp, white over the dark ends.
        fill: idx >= 2 && idx <= 4 ? '#1a1a1a' : '#ffffff',
      },
    }];

    // The callout is the label's own badge: a block arrow pointing back at the bar,
    // carrying the class letter. The registration-year mark is solid (that is the
    // official form); the current-scale mark is outlined so the pair reads as
    // "was → is" without needing a legend.
    const badge = (letter: string, caption: string, solid: boolean): unknown => {
      const bw = 50, bh = Math.max(30, h + 12), nose = 11;
      const bx = x + w + 12, by = y + h / 2 - bh / 2;
      const cx = bx + nose + (bw - nose) / 2;
      const parts: unknown[] = [{
        type: 'polygon',
        shape: {
          points: [
            [bx, by + bh / 2], [bx + nose, by], [bx + bw, by],
            [bx + bw, by + bh], [bx + nose, by + bh],
          ],
        },
        style: { fill: solid ? ink : 'transparent', stroke: ink, lineWidth: solid ? 0 : 1.5, lineDash: solid ? undefined : [3, 3] },
      }, {
        type: 'text',
        style: {
          x: cx, y: caption ? by + bh * 0.36 : by + bh / 2, text: letter,
          textAlign: 'center', textVerticalAlign: 'middle',
          fontFamily: sans, fontWeight: 700, fontSize: 15,
          fill: solid ? surface : ink,
        },
      }];
      // The caption rides INSIDE the badge under the letter. Outside it would collide
      // with the neighbouring bar, since the bars are only a row apart.
      if (caption) {
        parts.push({
          type: 'text',
          style: {
            x: cx, y: by + bh * 0.74, text: caption,
            textAlign: 'center', textVerticalAlign: 'middle',
            fontFamily: mono, fontSize: 9,
            fill: solid ? surface : muted,
            opacity: solid ? 0.85 : 1,
          },
        });
      }
      return { type: 'group', children: parts };
    };
    if (isMark) children.push(badge(classes[idx], c.markLabel || '', true));
    if (isToday && !isMark) children.push(badge(classes[idx], c.todayLabel || '', false));

    return { type: 'group', children };
  };

  return {
    grid: { left: 4, right: 78, top: 10, bottom: 14, containLabel: true },
    xAxis: { type: 'value', min: 0, max: 1, show: false },
    yAxis: {
      // inverse: ECharts category axes run bottom-up, but the Energieetikette is read
      // A at the top, G at the bottom – the direction is part of the artefact.
      type: 'category', data: classes, inverse: true,
      axisLine: { show: false }, axisTick: { show: false },
      axisLabel: { show: false }, splitLine: { show: false },
    },
    tooltip: { show: false },
    series: [{ type: 'custom', renderItem, data, silent: true, animationDuration: 500 }],
  } as ChartConfig;
}

/**
 * Expand a { type:'gantt', rows, window } config into a real ECharts custom-series
 * Gantt (§81): one rounded bar per row on a category y-axis (car names in the
 * gutter) against a time x-axis. Still-owned bars fade at the trailing edge. Rows
 * carry their date range + full tooltip text pre-localized from the server.
 */
function ganttConfig(cfg: ChartConfig, ctx: KindContext): ChartConfig {
  const ec = ctx.echarts;
  const rows = (cfg.rows as GanttRow[]) || [];
  const bars = (cfg.bars as GanttBar[]) || [];
  const win = (cfg.window as number[]) || [];
  const names = rows.map((r) => r.name);
  const inkOnAccent = ctx.color('--accent-ink');
  const mono = ctx.css('--font-mono') || 'ui-monospace, monospace';
  const sans = ctx.css('--font-sans') || 'system-ui, sans-serif';

  // One custom-series item per bar; multiple bars can share a row (a re-bought
  // car). value: [rowIndex, startMs, endMs, ongoing?1:0].
  const data = bars.map((b) => ({
    value: [b.row, b.start, b.end, b.ongoing ? 1 : 0],
    itemStyle: { color: 'var(--accent)' },
  }));

  const renderItem = (params: CustomRenderItemParams, api: CustomRenderItemAPI): unknown => {
    const idx = api.value(0);
    const start = api.coord([api.value(1), idx]);
    const end = api.coord([api.value(2), idx]);
    const ongoing = api.value(3) === 1;
    const bandH = api.size([0, 1])[1];
    const h = Math.max(6, Math.min(30, bandH * 0.55));
    const raw = { x: start[0], y: start[1] - h / 2, width: Math.max(1, end[0] - start[0]), height: h };
    const cs = params.coordSys;
    const g = ec.graphic;
    const rect = g && g.clipRectByRect
      ? g.clipRectByRect(raw, { x: cs.x, y: cs.y, width: cs.width, height: cs.height })
      : raw;
    if (!rect) return null;
    const baseStyle = api.style();
    let fill: unknown = baseStyle.fill;
    if (ongoing && typeof fill === 'string' && g && g.LinearGradient && ec.color && ec.color.modifyAlpha) {
      // Trailing fade so an open-ended (still-owned) bar reads as unbounded.
      fill = new g.LinearGradient(0, 0, 1, 0, [
        { offset: 0, color: fill },
        { offset: 0.82, color: fill },
        { offset: 1, color: ec.color.modifyAlpha(fill, 0.12) },
      ]);
    }
    const children: unknown[] = [{
      type: 'rect',
      shape: { ...rect, r: 4 },
      style: { ...baseStyle, fill },
    }];
    const range = bars[params.dataIndex as number]?.range;
    if (rect.width > 112 && range) {
      children.push({
        type: 'text',
        x: rect.x + 8,
        y: rect.y + rect.height / 2,
        style: { text: range, fill: inkOnAccent, font: `11px ${mono}`, align: 'left', verticalAlign: 'middle' },
      });
    }
    return { type: 'group', children };
  };

  return {
    tooltip: {
      trigger: 'item',
      formatter: (p: { dataIndex: number }): string => (bars[p.dataIndex]?.tip || '').replace(/\n/g, '<br/>'),
    },
    grid: { left: 8, right: 16, top: 8, bottom: 24, containLabel: true },
    xAxis: { type: 'time', min: win[0], max: win[1], axisLine: { show: false }, axisTick: { show: false } },
    yAxis: {
      type: 'category',
      data: names,
      inverse: true,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { fontFamily: sans, width: 132, overflow: 'truncate' },
    },
    series: [{ type: 'custom', renderItem, encode: { x: [1, 2], y: 0 }, data }],
  } as ChartConfig;
}

/** The server's thousands rule: apostrophe for Swiss languages, comma for English. */
function formatNumber(n: number, lang: string): string {
  if (typeof n !== 'number' || !isFinite(n)) return String(n);
  const sep = lang.slice(0, 2).toLowerCase() === 'en' ? ',' : '\'';
  const s = Math.abs(n).toString();
  const dot = s.indexOf('.');
  const int = dot === -1 ? s : s.slice(0, dot);
  const frac = dot === -1 ? '' : s.slice(dot);
  return (n < 0 ? '-' : '') + int.replace(/\B(?=(\d{3})+(?!\d))/g, sep) + frac;
}

setNumberFormat(formatNumber);
defineChartKind('census', censusConfig);
defineChartKind('energylabel', energyLabelConfig);
defineChartKind('gantt', ganttConfig);
